import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPen } from '@fortawesome/sharp-solid-svg-icons';
import { useRouter } from 'next/navigation';
import React, { useMemo } from 'react';

import { ButtonSize, ButtonVariant } from '@/components/button';
import IconButton from '@/components/button/iconButton';
import Typography from '@/components/typography';
import { LAYERS_URL } from '@/constants/urls';
import {
  LayerLibraryColor,
  LayerLibraryGradientColor,
} from '@/features/layerLibrary/colors';
import {
  ArdaCircleLayer,
  ArdaLayerClass,
  ArdaLineLayer,
  ArdaPolygonLayer,
} from '@/features/layerLibrary/models';

type Props = {
  layer: ArdaLayerClass;
};

export const LayerLibraryOverlayLegend: React.FC<Props> = ({ layer }) => {
  const router = useRouter();

  const mainColor: LayerLibraryColor | null = useMemo(() => {
    if (layer?.isPolygonLayer) {
      return (layer as ArdaPolygonLayer).fillColor ?? null;
    }
    if (layer?.isLineLayer) {
      return (layer as ArdaLineLayer).lineColor ?? null;
    }
    if (layer?.isCircleLayer) {
      return (layer as ArdaCircleLayer).circleColor ?? null;
    }
    return null;
  }, [layer]);

  const distributionStrategy: string | null = useMemo(() => {
    if (!mainColor || mainColor?.type === 'simple') return null;

    const gradientColor = mainColor as LayerLibraryGradientColor;

    if (gradientColor.binary) {
      return null;
    }

    if (gradientColor.distributionStrategy === 'equalCount') {
      return 'Equal Count';
    }

    if (gradientColor.distincts) {
      return null;
    }

    // Equal interval is default
    return 'Equal Interval';
  }, [mainColor]);

  /**
   * We are determining the type the same way as we determine the distribution strategy because we don't store
   *   the field type but can infer it based on how the data is distributed
   */
  const fieldOfInterestType: string | null = useMemo(() => {
    if (!mainColor || mainColor?.type === 'simple') return null;

    const gradientColor = mainColor as LayerLibraryGradientColor;

    if (gradientColor.binary) {
      return '(boolean)';
    }

    if (gradientColor.distributionStrategy === 'equalCount') {
      return '(number)';
    }

    if (gradientColor.distincts) {
      return '(string)';
    }

    // Equal interval is default
    return '(number)';
  }, [mainColor]);

  const colorMap: any[] | null = useMemo(() => {
    if (!mainColor || !layer) return null;

    if (mainColor.type === 'simple') {
      return [{ color: mainColor.color, value: 'All' }];
    }

    if (mainColor.type === 'gradient') {
      return layer.buildColorGradientLegend(mainColor);
    }

    return null;
  }, [layer, mainColor]);

  return (
    <div className="flex flex-col gap-y-3">
      <div className="flex items-center justify-between">
        <Typography className="truncate whitespace-nowrap" variant="button2">
          {(mainColor as LayerLibraryGradientColor)?.fieldOfInterest ??
            'Legend'}{' '}
          {fieldOfInterestType ?? ''}
        </Typography>

        <IconButton
          className="text-gray-70"
          size={ButtonSize.XS}
          variant={ButtonVariant.GHOST}
          onClick={() => {
            router.push(`${LAYERS_URL}/${layer.id}/appearance`);
          }}
        >
          <FontAwesomeIcon icon={faPen} />
        </IconButton>
      </div>

      {distributionStrategy && (
        <Typography variant="caption">{distributionStrategy}</Typography>
      )}
      {((mainColor as LayerLibraryGradientColor).minValue ||
        (mainColor as LayerLibraryGradientColor).maxValue) &&
        distributionStrategy === 'Equal Interval' && (
          <Typography variant="caption">
            Range of Interest:{' '}
            {(mainColor as LayerLibraryGradientColor).minValue} -{' '}
            {(mainColor as LayerLibraryGradientColor).maxValue}
          </Typography>
        )}

      <div className="flex flex-col gap-y-2">
        {colorMap?.map((c) => (
          <div
            key={`${c.color}-${c.value}`}
            className="flex h-5 items-center gap-x-3 truncate"
          >
            <div
              className="h-4 w-4 min-w-4 rounded-sm border border-2 border-white"
              style={{ backgroundColor: c.color }}
            />
            <Typography className="truncate" title={c.value} variant="body2">
              {c.value}
            </Typography>
          </div>
        ))}
      </div>
    </div>
  );
};
